import type { FC } from 'react'
import type { PostSummary } from '@/types'

import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { Hashtag } from '@/components/kit/hashtag'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Spark } from '@/components/kit/spark'

interface PostCardProps {
    post: PostSummary
    className?: string
    headingLevel?: 'h2' | 'h3'
}

export const PostMeta: FC<{ post: PostSummary, className?: string }> = ({ post, className }) => {
    return (
        <p className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-stone', className)}>
            {post.category && (
                <>
                    <span
                        className='text-ink'
                        data-mdlfy-cms={`categories:${post.category.id}:name`}
                    >
                        {post.category.name}
                    </span>
                    <Spark className='size-2 text-ember' />
                </>
            )}

            {post.published_at && (
                <time dateTime={post.published_at}>
                    {formatDate(post.published_at)}
                </time>
            )}

            {post.read_minutes && (
                <>
                    <Spark className='size-2 text-ember' />
                    <span>
                        <span data-mdlfy-cms={`posts:${post.id}:read_minutes`}>{post.read_minutes}</span> min read
                    </span>
                </>
            )}
        </p>
    )
}

export const CoverFallback: FC<{ title: string }> = ({ title }) => {
    return (
        <div className='grain absolute inset-0 flex items-end bg-sand p-6'>
            <Spark className='absolute right-6 top-6 size-10 text-ember' />
            <span
                aria-hidden='true'
                className='font-heading text-[5rem] font-bold leading-none tracking-[-0.04em] text-ink/15'
            >
                {title.slice(0, 1)}
            </span>
        </div>
    )
}

export const PostCard: FC<PostCardProps> = ({ post, className, headingLevel = 'h3' }) => {
    const Heading = headingLevel
    const href = `/journal/${post.slug}`

    return (
        <article className={cn('group relative flex h-full flex-col', className)}>
            <PageLink
                href={href}
                data-cursor='Read'
                tabIndex={-1}
                aria-hidden='true'
                className='relative block aspect-[4/3] overflow-hidden rounded-[1.5rem] bg-sand'
            >
                {post.cover ? (
                    <Photo
                        src={post.cover}
                        alt={post.cover_alt ?? ''}
                        sizes='(min-width: 1280px) 420px, (min-width: 768px) 45vw, 100vw'
                        className='absolute inset-0'
                        imgClassName='transition-transform duration-[1.2s] ease-expo group-hover:scale-[1.06]'
                    />
                ) : (
                    <CoverFallback title={post.title} />
                )}

                {post.hashtags.length > 0 && (
                    <span className='absolute left-4 top-4 flex flex-wrap gap-1.5'>
                        {post.hashtags.slice(0, 2).map(tag => (
                            <Hashtag
                                key={tag}
                                tag={tag}
                            />
                        ))}
                    </span>
                )}
            </PageLink>

            <PostMeta
                post={post}
                className='mt-5'
            />

            <Heading className='mt-3 text-[1.6rem] font-bold leading-[1.04] tracking-[-0.035em]'>
                <PageLink
                    href={href}
                    className='bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_2px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-500 ease-expo group-hover:bg-[length:100%_2px]'
                >
                    <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                </PageLink>
            </Heading>

            {post.excerpt && (
                <p
                    className='mt-3 line-clamp-3 text-[15px] leading-relaxed text-stone'
                    data-mdlfy-cms={`posts:${post.id}:excerpt`}
                >
                    {post.excerpt}
                </p>
            )}

            <PageLink
                href={href}
                className='mt-auto inline-flex items-center gap-2 pt-5 text-sm font-medium text-ink'
            >
                Read the story
                <span className='sr-only'>: {post.title}</span>
                <span className='flex size-7 items-center justify-center rounded-full bg-ink text-cream transition-[transform,background-color,color] duration-500 ease-expo group-hover:translate-x-1 group-hover:bg-ember group-hover:text-ink'>
                    <ArrowRight className='size-3.5' />
                </span>
            </PageLink>
        </article>
    )
}
